import { useMemo } from 'react';
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { concatPagination } from '@apollo/client/utilities';
import merge from 'deepmerge';
import isEqual from 'lodash.isequal';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { ON_SERVER } from 'helpers/context';
import { getUserToken } from 'helpers/authentication';
import fetch from 'isomorphic-unfetch';

type ApolloClientType = ApolloClient<Record<string, unknown>>;

const PL_DEVICE = 'web';
const APOLLO_URI = `${process.env.API_BASE_URL}/gql`;
const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';
let apolloClient: ApolloClientType;

const cmsLink = new HttpLink({
  uri: 'https://api-us-east-1.graphcms.com/v2/ckbjhy22m02f001w9fz0bcbzv/master',
  fetch,
});

const getLink = (token?: string): ApolloLink => {
  const httpLink = createHttpLink({
    uri: ON_SERVER ? APOLLO_URI : `/api/graphql`,
    fetch,
  });

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: getUserToken(token),
      },
    };
  });

  if (ON_SERVER) {
    return new RetryLink().split(
      (operation) => operation.getContext().clientName === 'cms',
      cmsLink,
      authLink.concat(httpLink),
    );
  }

  const retryLink = new RetryLink({
    attempts: {
      max: 30,
      retryIf: (error) => Boolean(error),
    },
    delay: {
      initial: 200,
      max: Infinity,
      jitter: true,
    },
  });

  const directionalLink = new RetryLink().split(
    (operation) => operation.getContext().clientName === 'cms',
    retryLink.concat(cmsLink),
    retryLink.concat(authLink.concat(httpLink)),
  );

  return directionalLink;
};

const createApolloClient = (token): ApolloClientType =>
  new ApolloClient({
    ssrMode: ON_SERVER,
    link: getLink(token),
    name: PL_DEVICE,
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            allPosts: concatPagination(),
          },
        },
      },
    }),
  });

const initializeApollo = (
  initialState = null,
  token?: string,
): ApolloClientType => {
  const tempApolloClient = apolloClient ?? createApolloClient(token);

  if (initialState) {
    const existingCache = tempApolloClient.extract();
    const data = merge(initialState, existingCache, {
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s)),
        ),
      ],
    });
    tempApolloClient.cache.restore(data);
  }

  if (ON_SERVER) return tempApolloClient;

  if (!apolloClient) apolloClient = tempApolloClient;
  return tempApolloClient;
};

const addApolloState = (
  client: ApolloClientType,
  pageProps: Record<string, Record<string, any>>, // eslint-disable-line @typescript-eslint/no-explicit-any
): Record<string, unknown> => {
  const currentProps = pageProps?.props ? pageProps.props : {};

  return {
    ...pageProps,
    props: {
      ...currentProps,
      [APOLLO_STATE_PROP_NAME]: client.cache.extract(),
    },
  };
};

const useApollo = (
  pageProps: Record<string, Record<string, any>>, // eslint-disable-line @typescript-eslint/no-explicit-any
  token?: string,
): ApolloClientType => {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  const store = useMemo(() => initializeApollo(state, token), [state, token]);
  return store;
};

export { APOLLO_STATE_PROP_NAME, initializeApollo, addApolloState, useApollo };
export type { ApolloClientType };
